// ---------------------------------------------------------------------------
// useTrafficEtas — fetch live-traffic ETAs for a set of candidate routes and
// return them keyed by classification. Used so the route-card list can show a
// "+X min traffic" pill and so the recommendation logic can demote a route
// whose "fastest" label evaporates in real-world congestion.
//
// Why a hook: routes refresh as the user changes destination, mode, or
// pleasantness profile. The hook handles cancellation across those refreshes,
// memoises by polyline identity, and never blocks the route list from
// rendering — the cards appear immediately and the traffic pill fills in when
// the network call resolves.
// ---------------------------------------------------------------------------

import { useEffect, useState } from "react";
import { fetchTrafficEta, type TrafficEta } from "@/api/mapboxTraffic";
import { fetchGoogleTimedEta } from "@/api/googleDirections";
import type { ClassifiedRoute } from "@/utils/scoring";

export type TrafficEtaMap = Record<string, TrafficEta | null>;

export const useTrafficEtas = (
  routes: ClassifiedRoute[] | undefined,
  /** Google via-timing only makes sense for driving; other modes stay
   *  Mapbox-only. Defaults to driving for backward compatibility. */
  mode: string = "driving",
): TrafficEtaMap => {
  const [etas, setEtas] = useState<TrafficEtaMap>({});

  useEffect(() => {
    if (!routes || routes.length === 0) {
      setEtas({});
      return;
    }
    let cancelled = false;
    // Mapbox handles tens of these comfortably and the rest of the UI doesn't
    // depend on them, so we don't gate render. The GOOGLE half is rate-limited
    // inside fetchGoogleTimedEta (in-flight memo + one retry) — Aug 8: this
    // effect re-runs on every card change, and generation reclassifies cards
    // repeatedly over ~28s, which burst-invoked the edge function until the
    // platform started answering 502 before boot.
    Promise.all(
      routes.map(async (r) => {
        // Both providers in parallel. Google's traffic model is the best
        // available, so when it successfully times OUR polyline (via-point
        // pinned; old deployments refuse via fetchGoogleTimedEta's guard)
        // its duration becomes the headline number. Mapbox still supplies
        // the per-leg congestion detail — and remains the sole source
        // whenever Google can't answer.
        const [eta, g] = await Promise.all([
          fetchTrafficEta(r.route.coordinates),
          mode === "driving" ? fetchGoogleTimedEta(r.route.coordinates) : Promise.resolve(null),
        ]);
        if (g) {
          // Sanity: reject Google times wildly off the route length
          // (badly-snapped via points) — >2x or <0.4x Mapbox's read.
          const plausible = !eta ||
            (g.durationS < eta.durationS * 2 && g.durationS > eta.durationS * 0.4);
          if (plausible) {
            const merged: TrafficEta = eta
              ? { ...eta, durationS: g.durationS }
              : { durationS: g.durationS, distanceM: g.distanceM, freeFlowS: null, legs: [] };
            return [r.classification, merged] as const;
          }
        }
        return [r.classification, eta] as const;
      }),
    ).then((pairs) => {
      if (cancelled) return;
      const next: TrafficEtaMap = {};
      for (const [cls, eta] of pairs) next[cls] = eta;
      setEtas(next);
    });
    return () => {
      cancelled = true;
    };
  }, [routes, mode]);

  return etas;
};
